// @ts-strict-ignore
import { isActionOfType } from 'flow/service/actions';
import { createPredicatedProcessor } from '../../lib/predicatedProcessor';
import type {
  ActionAddedChange,
  ActionModifiedChange,
  ActionRemovedChange,
  ActionRuleGroupingModifiedChange,
  Change,
  ChangelogInputBundle,
} from '../../types';
import { Operation } from '../../types';
import type { MergeBuffer, MergeBufferItem } from './types';
import { ActionType } from 'flow/store/engine/constants';


export function createChangeProcessors(args: {
  A: ChangelogInputBundle;
  B: ChangelogInputBundle;
  entriesBuffer: MergeBuffer;
  formattedOperation: Operation;
}) {
  const { entriesBuffer, formattedOperation } = args;

  const createChangeProcessor = createPredicatedProcessor<Change>;

  // note: snippet actions should not be included in the diff result
  return {
    actionAddedOrModified: createChangeProcessor({
      operator: 'some',
      predicates: [
        ({ descriptor, op, value }) => (
          descriptor === 'action'
          && op === Operation.Add
          && !isActionOfType(value.next, ActionType.SNIPPET)
        ),
        ({ descriptor, op, value }) => (
          descriptor === 'action'
          && (op === Operation.Modify)
          && !isActionOfType(value.next, ActionType.SNIPPET)
        ),
      ],
      process: (
        addedActionChanges?: ActionAddedChange[],
        modifiedActionChanges?: ActionModifiedChange[],
      ) => {
        const entries: MergeBufferItem[] = [
          ...(addedActionChanges ?? []),
          ...(modifiedActionChanges ?? []),
        ].flatMap(change => {
          const { value: { next: action } } = change;

          return action.publicId;
        });

        entriesBuffer.append(entries);
      },
    }),
    rulesModified: createChangeProcessor({
      predicates: [
        ({ descriptor, op }) => (descriptor === 'action.rules.grouping') && (op === Operation.Modify),
      ],
      process: (
        rulesModifiedChanges?: ActionRuleGroupingModifiedChange[],
      ) => {
        const entries: MergeBufferItem[] = rulesModifiedChanges.flatMap(change => {
          const {
            actionPublicId,
            value: { prev: previousRuleIdSet, next: nextRuleIdSet },
          } = change;

          // We can consider rules that exist in both the previous and next rule sets as
          // rules that remained the same across this change. To get only the newly added rules,
          // we must filter rules that exist in both rule sets from the newer rule set.

          const unchangedRuleIds = previousRuleIdSet.intersection(nextRuleIdSet);
          const nextRuleIds = Array.from(nextRuleIdSet);

          const newRuleIds = nextRuleIds.filter(ruleId => !unchangedRuleIds.has(ruleId));

          // We append the action id to account for when we add a rule set to an existing action with no rule set.
          // This case does not trigger an action modified change, as the operation processors see it as a purely additive op.
          // Since the formatters eventually get stored into sets, which prunes duplicates,
          // we do not need to worry about duplicate action ids from being present within our computed mergeDiff.
          if (formattedOperation === Operation.Modify && previousRuleIdSet.size === 0) {
            return [...newRuleIds, actionPublicId];
          }

          return newRuleIds;
        });

        entriesBuffer.append(entries);
      },
    }),
    actionRemoved: createChangeProcessor({
      predicates: [
        ({ descriptor, op, value }) => (
          descriptor === 'action'
          && op === Operation.Remove
          && !isActionOfType(value.prev, ActionType.SNIPPET)
        ),
      ],
      process: (removedActionChanges?: ActionRemovedChange[]) => {
        const entries: MergeBufferItem[] = (removedActionChanges ?? []).map(
          change => change.actionPublicId,
        );
        entriesBuffer.append(entries);
      },
    }),
  };
}
